import { DataGrid, DataGridColumn } from '@cimplico/components';
import { useState } from 'react';
import { ROW_SIZES } from '../../constants';
import { useGroups } from '../../queries/use-groups';
import { Group } from '../../types/group';
import GroupRow from './GroupRow';
import EmptyStateNew from '../../components/empty-state/EmptyStateNew';
import NoResults from '../../components/empty-state/NoResults';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { useIsFirmRole } from '../../hooks/use-is-firm-role';

const columns: DataGridColumn[] = [
    {
        header: 'Group',
        alignment: 'left',
        width: '1fr',
    },
    {
        header: 'Entities',
        alignment: 'center',
        width: '1fr',
    },
    {
        header: 'Last Updated',
        alignment: 'left',
        width: '1fr',
    },
    {
        header: 'Open Workpapers',
        alignment: 'center',
        width: '1fr',
    },
    {
        header: 'Providers',
        alignment: 'center',
        width: '0.5fr',
    },
];

type GroupsTableProps = {
    search?: string;
    sort?: string;
    filter?: string;
    onSelect?: (group: Group[]) => void;
};

function GroupsTable({ search = '', sort = '', filter = '', onSelect }: GroupsTableProps): JSX.Element {
    const { isExternalUser } = useIsFirmRole();
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(ROW_SIZES[0]);

    const showEmptyNew = !search;
    const groupsQuery = useGroups({ page, limit, search, sort, filter });

    function selectGroups(ids: string[]): void {
        const selectedGroups = groups.filter((g) => ids.includes(g.id)) ?? [];
        onSelect && onSelect(selectedGroups);
    }

    if (groupsQuery.isLoading) {
        return <></>;
    }
    // TODO: Create an error widget
    if (groupsQuery.isError || !groupsQuery.isSuccess) {
        return <p>Error!</p>;
    }

    const { groups, totalItems } = groupsQuery.data;

    return (
        <div>
            <DataGrid
                columns={columns}
                rowSizes={ROW_SIZES}
                totalRows={totalItems}
                onPageChange={setPage}
                onRowSizeChange={setLimit}
                onSelect={selectGroups}
                isLoading={groupsQuery.isFetching}
                emptyMessage={
                    showEmptyNew ? (
                        <EmptyStateNew
                            disableActionButton={isExternalUser}
                            icon={<BuildingOffice2Icon />}
                            navigateTo="/groups/new"
                            title="Add your first Group"
                            actionText="New Group"
                            helpLink="https://learn.cimplico.com/docs/creating-groups"
                            helpText="Organise your related entities into a group to facilitate inter-entity actions."
                        />
                    ) : (
                        <NoResults search={search} />
                    )
                }
            >
                {groups.map((group, idx) => {
                    return <GroupRow key={idx} group={group} id={group.id} />;
                })}
            </DataGrid>
        </div>
    );
}

export default GroupsTable;
